/**
 * 清新薄荷模版 - 详情页（v4）
 * 
 * 设计核心理念：与红色模版（全宽居中单栏）完全不同的双栏布局
 * - 左侧主内容区（~65%）：图片轮播 + 详细描述
 * - 右侧固定栏（~35%）：联系信息卡片 + 相关推荐 + 发布者信息
 * - 类似新闻/博客的侧边栏布局
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/teal/components/Navbar";
import Footer from "@/templates/teal/components/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function TealListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [showPhone, setShowPhone] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => window.location.replace("/category"))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => {
        setRelatedListings(
          (data.list || []).filter((l) => l.id !== id).slice(0, 5)
        );
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images?.length > 0 ? listing.images : [];
  const hasContact = listing.contact_phone || listing.contact_wechat;

  return (
    <div className="min-h-screen bg-[#f8fafb]">
      <Navbar />

      <div className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 面包屑 */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
            <Link to="/" className="hover:text-teal-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <Link to="/category" className="hover:text-teal-600 transition-colors">全部分类</Link>
            {listing.category_name && (
              <>
                <i className="ri-arrow-right-s-line text-[10px]" />
                <span>{listing.category_name}</span>
              </>
            )}
            <i className="ri-arrow-right-s-line text-[10px]" />
            <span className="text-gray-600 truncate">{listing.title}</span>
          </nav>

          {/* ── 双栏布局（与红色模版的全宽单栏最大差异）── */}
          <div className="flex flex-col lg:flex-row gap-6">
            
            {/* ── 左侧：主内容区 ── */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                {/* 图片轮播 */}
                {images.length > 0 && (
                  <div className="relative">
                    <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
                      <img
                        src={images[currentImage]}
                        alt={`图${currentImage + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImage((p) => (p === 0 ? images.length - 1 : p - 1))}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors cursor-pointer"
                        >
                          <i className="ri-arrow-left-s-line text-gray-700 text-lg" />
                        </button>
                        <button
                          onClick={() => setCurrentImage((p) => (p === images.length - 1 ? 0 : p + 1))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors cursor-pointer"
                        >
                          <i className="ri-arrow-right-s-line text-gray-700 text-lg" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                          {images.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setCurrentImage(i)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                i === currentImage ? "bg-teal-500 w-6" : "bg-white/70 hover:bg-white"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* 缩略图导航 */}
                {images.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto border-b border-gray-50">
                    {images.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          i === currentImage ? "border-teal-500 shadow-sm" : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* 标题与元信息 */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {listing.is_pinned && (
                      <span className="px-2.5 py-1 bg-teal-600 text-white text-[11px] font-bold rounded-lg flex items-center gap-1">
                        <i className="ri-pushpin-line text-[10px]" />置顶
                      </span>
                    )}
                    {listing.category_name && (
                      <span className="px-2.5 py-1 bg-teal-50 text-teal-600 text-[11px] font-medium rounded-lg">
                        {listing.category_name}
                      </span>
                    )}
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-800 leading-snug mb-4">{listing.title}</h1>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(listing.created_at)}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.view_count} 次浏览</span>
                    <span className="flex items-center gap-1"><i className="ri-refresh-line" />{formatTimeSince(listing.updated_at || listing.created_at)}</span>
                  </div>
                </div>

                {/* 详细介绍 */}
                <div className="px-6 sm:px-8 pb-8">
                  <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-gradient-to-b from-teal-400 to-teal-600 rounded-full" />
                    详细介绍
                  </h2>
                  <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-xl p-5">
                    {listing.content || "暂无详细描述。"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── 右侧：侧边栏（与红色全宽布局的最大差异）── */}
            <aside className="w-full lg:w-[340px] shrink-0 space-y-4">
              
              {/* 联系信息卡片 */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-emerald-600 px-5 py-4">
                  <h3 className="text-white font-bold flex items-center gap-2">
                    <i className="ri-user-3-line" />
                    联系发布者
                  </h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-lg font-bold shadow-md">
                      {(listing.contact_name || "匿").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{listing.contact_name || "匿名用户"}</p>
                      {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <i className="ri-building-line text-[10px]" />{listing.company_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {listing.address && (
                    <div className="flex items-start gap-2 mb-3 text-xs text-gray-500">
                      <i className="ri-map-pin-line text-teal-500 mt-0.5" />
                      <span>{listing.address}</span>
                    </div>
                  )}

                  {hasContact && (
                    <div className="space-y-2.5">
                      {listing.contact_phone && (
                        showPhone ? (
                          <a
                            href={`tel:${listing.contact_phone}`}
                            className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-colors text-sm"
                          >
                            <i className="ri-phone-line" />{listing.contact_phone}
                          </a>
                        ) : (
                          <button
                            onClick={() => setShowPhone(true)}
                            className="flex items-center justify-center gap-2 w-full py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-colors text-sm cursor-pointer"
                          >
                            <i className="ri-phone-line" />查看联系电话
                          </button>
                        )
                      )}
                      {listing.contact_wechat && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(listing.contact_wechat).catch(() => {});
                            alert("微信号已复制：" + listing.contact_wechat);
                          }}
                          className="flex items-center justify-center gap-2 w-full py-3 bg-green-50 text-green-700 font-bold rounded-xl border border-green-200 hover:bg-green-100 transition-colors text-sm cursor-pointer"
                        >
                          <i className="ri-wechat-line" />复制微信号
                        </button>
                      )}
                    </div>
                  )}

                  {/* 安全提示 */}
                  <div className="mt-4 p-3 bg-amber-50 rounded-xl text-[11px] text-amber-700 flex items-start gap-2">
                    <i className="ri-shield-check-line mt-0.5" />
                    <span>交易时请核实对方身份，谨防诈骗。如遇虚假信息请及时举报。</span>
                  </div>
                </div>
              </div>

              {/* 相关推荐 */}
              {relatedListings.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-50">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-teal-500" />
                      相关推荐
                    </h3>
                  </div>
                  <div className="p-3 space-y-1">
                    {relatedListings.map((item) => (
                      <Link
                        key={item.id}
                        to={`/listing/${item.id}`}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-teal-50 transition-colors group"
                      >
                        <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                          {item.images?.[0] ? (
                            <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-image-line text-gray-300 text-lg" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-medium text-gray-800 line-clamp-1 group-hover:text-teal-600 transition-colors">
                            {item.title}
                          </h4>
                          <span className="text-[11px] text-gray-400 mt-1 block">
                            {item.category?.name || "未分类"}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* 发布按钮 */}
              <Link
                to="/publish"
                className="block w-full py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-center font-bold rounded-2xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40"
              >
                <i className="ri-add-line mr-1.5" />免费发布信息
              </Link>

            </aside>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatTimeSince(dateStr: string): string {
  const d = new Date(dateStr);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "今天更新";
  if (days === 1) return "昨天更新";
  if (days < 7) return `${days}天前更新`;
  return `${d.getMonth() + 1}月${d.getDate()}日更新`;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#f8fafb]">
      <Navbar />
      <div className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-pulse">
          <div className="w-48 h-3 bg-gray-200 rounded mb-5" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="aspect-[16/9] bg-gray-200" />
                <div className="p-6 sm:p-8 space-y-3">
                  <div className="w-3/4 h-7 bg-gray-200 rounded" />
                  <div className="w-1/3 h-4 bg-gray-100 rounded" />
                </div>
              </div>
            </div>
            <div className="w-full lg:w-[340px]">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
                <div className="w-20 h-5 bg-gray-200 rounded" />
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="space-y-1.5">
                    <div className="w-16 h-4 bg-gray-200 rounded" />
                    <div className="w-24 h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
